import _ from "lodash";

const FAST_YELLOW_THRESHOLD_PERCENT = 30;
const BOOK_YELLOW_TEMPLATE_ALIASES = ['EBOOK', 'READING_DELUXE'];
const FAST_YELLOW_SUBJECT_NAMES = ['phonics', 'writing'];

const normalizeText = (value) => (
  value === null || value === undefined
    ? ''
    : String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
);

const hasYellowOrGreenStar = (feedback) => !!(feedback && (feedback.partial || feedback.mastered));

const getPercent = (count, total) => (total > 0 ? (count / total) * 100 : 0);

export default ({ solutionMenu, reports, templates, learningCenterConfig, isTeam }) => {
  const newReports = JSON.parse(JSON.stringify(reports));

  const templateDefaultForReports = _.find(templates, {
    defaultForReports: true,
  });

  const { activities, activityGroups, menuGroups } = solutionMenu;
  const { percentActivities = "50%" } = learningCenterConfig;

  const numberPercentActivities = parseInt(percentActivities);

  const activitiesById = _.keyBy(activities || [], '_id');
  const activityGroupsById = _.keyBy(activityGroups || [], '_id');
  const templatesById = _.keyBy(templates || [], '_id');
  const menuEntriesByGroupId = _.keyBy(menuGroups || [], 'groupId');

  const getActivityTemplateAlias = (activityId) => {
    const activity = activitiesById[activityId];
    const template = activity && templatesById[activity.templateId];
    return template && template.alias;
  };

  const getLeafActivityIds = (groupId, visited = new Set()) => {
    if (!groupId || visited.has(groupId)) return [];
    visited.add(groupId);

    const group = activityGroupsById[groupId];
    if (!group || !Array.isArray(group.elements)) return [];

    return group.elements.reduce((acc, element) => {
      if (!element || !element._id) return acc;

      if (element.isGroup) {
        acc.push(...getLeafActivityIds(element._id, visited));
      } else {
        acc.push(element._id);
      }

      return acc;
    }, []);
  };

  const shouldUseThirtyPercentYellow = (group) => {
    if (!group) return false;
    if (group.generatedFromWordlist) return true;

    const menuEntry = menuEntriesByGroupId[group._id];
    if (!menuEntry) return false;

    const subjectText = normalizeText(menuEntry.firstCategory);
    const groupText = normalizeText(group.name);

    return FAST_YELLOW_SUBJECT_NAMES.some((subject) => (
      subjectText === subject ||
      subjectText.includes(subject) ||
      groupText.includes(subject)
    ));
  };

  const hasBookYellowTrigger = (group) => {
    if (!group || !Array.isArray(group.elements)) return false;

    return group.elements.some((element) => {
      if (!element || element.isGroup || !element._id) return false;

      const templateAlias = getActivityTemplateAlias(element._id);
      if (!BOOK_YELLOW_TEMPLATE_ALIASES.includes(templateAlias)) return false;

      return hasYellowOrGreenStar(reports[element._id]);
    });
  };

  const hasThirtyPercentYellow = (group) => {
    if (!shouldUseThirtyPercentYellow(group)) return false;

    const leafActivityIds = getLeafActivityIds(group._id);
    if (!leafActivityIds.length) return false;

    const starredCount = leafActivityIds.filter((activityId) => (
      hasYellowOrGreenStar(reports[activityId])
    )).length;

    return getPercent(starredCount, leafActivityIds.length) >= FAST_YELLOW_THRESHOLD_PERCENT;
  };

  const getItemFeedback = (itemId, isGroup) => {
    let feedback = reports[itemId];

    if (isGroup) {
      const group = activityGroupsById[itemId];
      const groupElements = (group && Array.isArray(group.elements)) ? group.elements : [];
      const elementsFeedbacks = groupElements.map(({ _id, isGroup }) =>
        getItemFeedback(_id, isGroup)
      );

      const elemsCount = groupElements.length;

      const masteredCount = elementsFeedbacks.filter((f) => f && f.mastered)
        .length;
      const partialCount = elementsFeedbacks.filter(
        (f) => f && (f.partial || f.mastered)
      ).length;

      const percentPartial = getPercent(partialCount, elemsCount);
      const percentMastered = getPercent(masteredCount, elemsCount);

      if (
        percentPartial >= numberPercentActivities ||
        hasBookYellowTrigger(group) ||
        hasThirtyPercentYellow(group)
      ) {
        feedback = { ...(feedback || {}), partial: true };
      }

      if (percentMastered >= numberPercentActivities) {
        feedback = { ...(feedback || {}), partial: true, mastered: true };
      }

      if (
        templateDefaultForReports &&
        group.generatedFromWordlist &&
        (!feedback || !feedback.mastered)
      ) {
        const activitiesInGroup = groupElements.map(({ _id }) =>
          activitiesById[_id]
        );

        const activityDefaultForReports = _.find(activitiesInGroup, {
          templateId: templateDefaultForReports._id,
        });

        const defaultActivityReports =
          (activityDefaultForReports &&
            reports[activityDefaultForReports._id]) ||
          {};

        if (defaultActivityReports.partial) {
          feedback = { ...(feedback || {}), partial: true };
        }

        if (defaultActivityReports.mastered) {
          feedback = { ...(feedback || {}), partial: true, mastered: true };
        }
      }
    }

    if (feedback) {
      // if is mastered, then add date when it was mastered
      if (feedback.mastered && !feedback.dateMastered) {
        feedback.dateMastered = Date.now();
      }

      if (feedback.mastered && isTeam) {
        feedback.masteredTogether = true;
      }

      newReports[itemId] = feedback;
    }

    return feedback;
  };

  menuGroups.forEach((group) => getItemFeedback(group.groupId, true));

  return newReports;
};
